import React from 'react'

import { get, isEmpty } from 'lodash'
import moment from 'moment'

import { Flex } from 'antd'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { setBillingCodes } from 'src/store/modules/admin/activityDashboard'
import { getPatientActivityLog } from 'src/store/modules/common'
import {
  getPatientBillingStatistics,
  getPatientProfile,
  patientUuidSelector,
  setPatientActivityLog
} from 'src/store/modules/patient'
import { getUserActivitiesCounters } from 'src/store/modules/user'

// constants
import {
  CREATE_PATIENT_ACTIVITY,
  DATE_TIME_FORMAT_FOR_SERVER,
  GET_BILLING_CODES,
  GET_BILLING_PROGRAMS,
  GET_PATIENT_ACTIVITY_LOG_EXPORT,
  GET_PATIENT_ACTIVITY_LOG_PDF_EXPORT,
  PATIENT_ACTIVITY
} from 'src/constants'
import { showErrorModal } from 'src/services/dialogs'
import { exportReport } from 'src/utils/Functions'

export const fetchPatientActivityLog =
  (config = {}, isFromModal) =>
  async (dispatch) => {
    try {
      const { items = [], meta = {} } = await getPatientActivityLog(config)

      if (isFromModal) return items

      dispatch(setPatientActivityLog(items))

      return meta
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const createPatientActivityLog =
  (config = {}) =>
  async (dispatch, getState) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: CREATE_PATIENT_ACTIVITY,
          params
        },
        payload
      })

      const patientUuid = patientUuidSelector(getState()) || null

      await Promise.all([
        dispatch(getUserActivitiesCounters()),
        patientUuid && dispatch(getPatientBillingStatistics(patientUuid)),
        patientUuid && dispatch(getPatientProfile(patientUuid))
      ])

      return true
    } catch (err) {
      if (err?.data?.length) {
        handleActivityOverlapErrorMessage(err)
        return false
      }

      if (err?.errors?.billingProgram) {
        handleActivityOverlapErrorMessage({
          data: err.errors.billingProgram
        })
        return false
      }

      catchError({
        ...err,
        message: handleServerError(err)
      })

      return false
    }
  }

export const editPatientActivity =
  (config = {}) =>
  async (dispatch, getState) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATIENT_ACTIVITY,
          params
        },
        payload
      })

      const patientUuid = patientUuidSelector(getState()) || null

      await Promise.all([
        dispatch(getUserActivitiesCounters()),
        patientUuid && dispatch(getPatientBillingStatistics(patientUuid)),
        patientUuid && dispatch(getPatientProfile(patientUuid))
      ])

      return true
    } catch (err) {
      if (err?.data?.length) {
        handleActivityOverlapErrorMessage(err)
        return false
      }

      if (err?.errors?.billingProgram) {
        handleActivityOverlapErrorMessage({
          data: err.errors.billingProgram
        })
        return false
      }
      catchError({
        ...err,
        message: handleServerError(err)
      })

      return false
    }
  }

export const deletePatientActivity =
  (config = {}) =>
  async (dispatch, getState) => {
    const { params = {} } = config

    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: PATIENT_ACTIVITY,
          params
        }
      })

      const patientUuid = patientUuidSelector(getState()) || null

      await Promise.all([
        dispatch(getUserActivitiesCounters()),
        patientUuid && dispatch(getPatientBillingStatistics(patientUuid)),
        patientUuid && dispatch(getPatientProfile(patientUuid))
      ])
    } catch (err) {
      catchError(err)
    }
  }

export const getBillingCodes = (uuid, query) => () => {
  try {
    return httpClient.request({
      method: 'get',
      url: {
        pathname: GET_BILLING_CODES,
        params: { uuid },
        query
      }
    })
  } catch (err) {
    catchError(err)
  }
}

export const fetchBillingPrograms = async (query = {}) => {
  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_BILLING_PROGRAMS,
        query
      }
    })
  } catch (err) {
    catchError(err)
    return []
  }
}

export const getBillingPrograms =
  (query = {}) =>
  async (dispatch) => {
    try {
      const codes = await fetchBillingPrograms(query)

      dispatch(setBillingCodes(codes))

      return codes
    } catch (err) {
      catchError(err)
    }
  }

export const handleServerError = (err) => {
  if (!isEmpty(get(err, 'errors', {}))) {
    return Object.keys(err.errors)
      .map((key) => get(err, ['errors', key, '0'], ''))
      .join(' ')
  } else {
    return get(err, 'message', '')
  }
}

export const exportPatientActivityLogReport = (config, type) => async () => {
  const updConfig = {
    pathname:
      type === 'xls'
        ? GET_PATIENT_ACTIVITY_LOG_EXPORT
        : GET_PATIENT_ACTIVITY_LOG_PDF_EXPORT,
    ...config
  }

  await exportReport(
    updConfig,
    `patient_activity_log_${moment().format(
      DATE_TIME_FORMAT_FOR_SERVER
    )}.${type}`
  )
}

const handleActivityOverlapErrorMessage = (err) =>
  showErrorModal({
    width: 600,
    title: 'Validation error',
    content: (
      <Flex vertical>
        {[<h4 key={1}>{err?.message}</h4>, ...(err?.data || [])].map((e) => (
          <span key={e}>{e}</span>
        ))}
      </Flex>
    )
  })
